import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { DEFAULT_IMAGE_FIT, IMAGE_FIT_COVER } from '../../../util/listingImageFit';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
// Product photos are mostly square or portrait. Anything close to square gets a
// square frame and anything taller gets a 4:5 frame, so these photos fill the
// gallery instead of sitting between wide white bars in a 4:3 box.
const MAX_SQUARE_ASPECT_RATIO = 1.1;
const MIN_SQUARE_ASPECT_RATIO = 0.9;
// Matches the "Viewport large" breakpoint: below it, tapping a photo zooms in.
const ZOOM_ON_TAP_MAX_WIDTH = 1023;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return !hasDimensions
    ? { aspectWidth: 1, aspectHeight: 1 }
    : aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : aspectRatio < MIN_SQUARE_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 5 }
    : aspectRatio <= MAX_SQUARE_ASPECT_RATIO
    ? { aspectWidth: 1, aspectHeight: 1 }
    : { aspectWidth: w, aspectHeight: h };
};

const isZoomOnTapViewport = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia(`(max-width: ${ZOOM_ON_TAP_MAX_WIDTH}px)`).matches;

/**
 * Full-screen view of one photo on phones and tablets. The photo is shown at
 * twice the screen width inside a scrollable frame, so the shopper can drag
 * around to look at a label or a fabric close up.
 */
const ZoomedImage = props => {
  const { image, alt, imageVariants, onClose, intl } = props;
  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div className={css.zoomOverlay} role="dialog" aria-modal="true" aria-label={alt}>
      <button
        type="button"
        className={css.zoomClose}
        onClick={onClose}
        aria-label={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <IconClose rootClassName={css.zoomCloseIcon} />
      </button>
      <div className={css.zoomScroller}>
        <ResponsiveImage
          rootClassName={css.zoomImage}
          image={image}
          alt={alt}
          variants={imageVariants}
          sizes="200vw"
        />
      </div>
      <p className={css.zoomHint}>
        <FormattedMessage id="ListingImageGallery.zoomHint" />
      </p>
    </div>
  );
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @param {string} [props.imageFit] - IMAGE_FIT_CONTAIN or IMAGE_FIT_COVER, chosen by the brand
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomedIndex, setZoomedIndex] = useState(null);
  const intl = useIntl();
  const {
    rootClassName,
    className,
    images,
    imageVariants,
    thumbnailVariants,
    imageFit = DEFAULT_IMAGE_FIT,
  } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // Full screen always shows the whole photo: that's where shoppers go to see
  // every detail.
  const isCover = imageFit === IMAGE_FIT_COVER;
  const itemClassName = isCover && !isFullscreen ? css.itemCover : css.item;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const thumbVariant = thumbVariants[0];
  // react-image-gallery jumps back to the first slide whenever the `items` prop
  // changes identity. Changing slides re-renders this component (currentIndex),
  // so the items must stay the same array between renders. The image ids are
  // used as the key so a parent passing a fresh but identical array doesn't
  // reset the gallery either.
  const imageIdsKey = images.map(img => img.id?.uuid).join(',');
  const items = useMemo(
    () =>
      images.map((img, i) => {
        return {
          // We will only use the image resource, but react-image-gallery
          // requires the `original` key from each item.
          original: '',
          alt: intl.formatMessage(
            { id: 'ListingImageGallery.imageAltText' },
            { index: i + 1, count: images.length }
          ),
          thumbAlt: intl.formatMessage(
            { id: 'ListingImageGallery.imageThumbnailAltText' },
            { index: i + 1, count: images.length }
          ),
          thumbnail: img.attributes?.variants?.[thumbVariant],
          image: img,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageIdsKey, thumbVariant, intl]
  );
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={itemClassName}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={classNames(css.thumb, { [css.thumbCover]: isCover })}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  // Fullscreen mode doesn't work on phones, so a tap there opens the zoom view.
  const handleSlideClick = () => {
    if (!isFullscreen && isZoomOnTapViewport()) {
      setZoomedIndex(currentIndex);
    }
  };
  const zoomedItem = zoomedIndex != null ? items[zoomedIndex] : null;

  return (
    <>
      <ReactImageGallery
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        onScreenChange={onScreenChange}
        onSlide={setCurrentIndex}
        onClick={handleSlideClick}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        renderFullscreenButton={renderFullscreenButton}
        // "1 / 5" over the photo, so shoppers on a phone know there are more
        // photos to swipe through.
        showIndex={items.length > 1}
        {...IMAGE_GALLERY_OPTIONS}
      />
      {zoomedItem ? (
        <ZoomedImage
          image={zoomedItem.image}
          alt={zoomedItem.alt}
          imageVariants={imageVariants}
          onClose={() => setZoomedIndex(null)}
          intl={intl}
        />
      ) : null}
    </>
  );
};

export default ListingImageGallery;
